import { Box, Drawer, DrawerContent, DrawerOverlay, Modal, ModalOverlay, ModalContent } from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { useIsMobile } from '../../helpers/useIsMobile';
import PbmCancelModal from '../pbm/PbmCancelModal';
import PbmConsentStep from './PbmConsentStep';
import PbmReviewStep from './PbmReviewStep';
import PbmSuccessStep from './PbmSuccessStep';
import PbmErrorStep from './PbmErrorStep';
import PbmAlreadyRegisteredStep from './PbmAlreadyRegisteredStep';
import { PbmService } from '../pbm/pbmService';
import { TrackerEventService } from '../../containers/prescription/trackerEventService';
import { getPbmBenefit, Medicine, PspProgramData } from '../../interfaces/medicine';
import { MedicineForEdition } from '../../interfaces/medicineEdition';
import { Patient, Prescriber } from '../../interfaces/users';
import { logger } from '../../helpers/logger';
import { toE164BrazilPhone } from '../../helpers/string';

type Step = 'consent' | 'review' | 'success' | 'error' | 'alreadyRegistered';

interface PbmSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  programName?: string;
  optoutUrl?: string;
  partnerProgramUrl?: string;
  controllerName?: string;
  patientEmail?: string;
  patientPhone?: string;
  onConfirm?: (patientConsent: boolean, professionalConsent: boolean, email?: string, phone?: string) => void;
  onCancel?: () => void;
  medicineData?: Medicine | MedicineForEdition;
  prescription?: {
    idPrescricao: number;
    Paciente?: Patient;
    Prescritor?: Prescriber;
    idPrescritor?: number;
    CodigoValidacao?: string;
  };
  partnerId?: number;
  programId?: number;
  eanCode?: string;
  programData?: PspProgramData;
  initialStep?: Step;
  onStepChange?: (step: Step) => void;
}

const PbmSidebar: React.FC<PbmSidebarProps> = ({
  isOpen,
  onClose,
  programName = '',
  optoutUrl = '',
  partnerProgramUrl = '',
  controllerName = '',
  patientEmail = '',
  patientPhone = '',
  onConfirm,
  onCancel,
  medicineData,
  prescription,
  partnerId,
  programId,
  eanCode,
  programData,
  initialStep,
  onStepChange,
}) => {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<Step>(() => {
    if (initialStep) return initialStep;
    if (programData?.registered) return 'alreadyRegistered';
    return 'consent';
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [email, setEmail] = useState(patientEmail);
  const [phone, setPhone] = useState(patientPhone);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const previousIsOpenRef = useRef<boolean>(isOpen);

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;

    if (!isOpen) {
      setEmail(patientEmail);
      setPhone(patientPhone);
      setIsLoading(false);
      setShowCancelModal(false);
    } else if (!wasOpen && isOpen) {
      let newStep: Step;
      if (initialStep) {
        newStep = initialStep;
      } else if (programData?.registered) {
        newStep = 'alreadyRegistered';
      } else {
        newStep = 'consent';
      }
      setStep(newStep);
      if (onStepChange) {
        onStepChange(newStep);
      }
      setConsentGiven(false);
      setEmail(patientEmail);
      setPhone(patientPhone);
      setIsLoading(false);
      setShowCancelModal(false);
    } else if (isOpen) {
      setEmail(patientEmail);
      setPhone(patientPhone);
    }
  }, [isOpen, patientEmail, patientPhone, initialStep, programData?.registered, onStepChange]);

  const handleConsentConfirm = async () => {
    if (!consentGiven) return;

    setStep('review');
    if (onStepChange) {
      onStepChange('review');
    }

    await handleEventEmission('backoffice_campaign_pbm_consent_click', {
      consentPaciente: consentGiven,
      flow: 'consentimento',
    });
  };

  const handleBackFromReview = () => {
    setStep('consent');
    if (onStepChange) {
      onStepChange('consent');
    }
  };

  const goToError = (context: Record<string, unknown>) => {
    logger.error('[PBM - PreEnrollment] Erro ao criar pré-cadastro:', context);
    setStep('error');
    if (onStepChange) {
      onStepChange('error');
    }
    if (onConfirm) {
      onConfirm(consentGiven, consentGiven, email, phone);
    }
  };

  const handlePbmPreEnrollment = async () => {
    await handleEventEmission('backoffice_campaign_pbm_contato_review_click', {
      flow: 'contato paciente',
      continouPreCadastro: true,
    });

    if (!medicineData) {
      goToError({ medicineData });
      return;
    }

    if (!prescription) {
      goToError({ message: 'prescrição ausente' });
      return;
    }

    if (!partnerId || partnerId <= 0) {
      goToError({ partnerId });
      return;
    }

    if (!programId || programId <= 0) {
      goToError({ programId });
      return;
    }

    if (!eanCode) {
      goToError({ eanCode });
      return;
    }

    const patient = prescription.Paciente;
    const patientId = patient?.idPaciente;
    const patientName = patient?.Nome || '';
    const patientDocument = patient?.Documento || '';
    const patientBirthdate = patient?.Nascimento || '';
    const doctor = prescription.Prescritor;
    const doctorId = doctor?.idPrescritor || prescription.idPrescritor;
    const doctorName = doctor?.Nome || '';
    const doctorDocument = doctor?.Documento || '';
    const doctorLicenseState = doctor?.UFConselho || '';
    const doctorLicenseNumber = doctor?.Numero || '';
    const doctorLicenseType = doctor?.Conselho || '';
    const doctorEmail = doctor?.Email || '';

    if (!patientId || patientId <= 0 || !patientName || !patientDocument || !patientBirthdate) {
      goToError({
        patientId,
        hasPatientName: !!patientName,
        hasPatientDocument: !!patientDocument,
        hasPatientBirthdate: !!patientBirthdate,
      });
      return;
    }

    if (
      !doctorId ||
      doctorId <= 0 ||
      !doctorName ||
      !doctorDocument ||
      !doctorLicenseState ||
      !doctorLicenseNumber ||
      !doctorLicenseType
    ) {
      goToError({
        doctorId,
        hasDoctorName: !!doctorName,
        hasDoctorDocument: !!doctorDocument,
        hasDoctorLicenseState: !!doctorLicenseState,
        hasDoctorLicenseNumber: !!doctorLicenseNumber,
        hasDoctorLicenseType: !!doctorLicenseType,
      });
      return;
    }

    if (!email || email.trim() === '') {
      goToError({ email });
      return;
    }

    if (!phone || phone.trim() === '') {
      goToError({ hasPhone: !!phone });
      return;
    }

    setIsLoading(true);

    try {
      const data = await PbmService.pbmPreEnrollment({
        eanCode,
        programId,
        partnerId,
        patientId,
        doctorId,
        prescriptionId: prescription.idPrescricao,
        phone: toE164BrazilPhone(phone),
        email: email.trim(),
        patientConsent: consentGiven,
        doctorConsent: consentGiven,
        patientName,
        patientDocument,
        patientBirthdate,
        doctorName,
        doctorDocument,
        doctorLicenseState,
        doctorLicenseNumber,
        doctorLicenseType,
        doctorEmail,
      });

      setIsLoading(false);
      if (!data?.isSuccess) {
        goToError({ error: data?.description });
        return;
      }

      if (medicineData?.IdMedicamento && programData) {
        const updatedProgramData = { ...programData, registered: true };
        const { usePrescriptionStore } = await import('../../stores/prescriptionStore');
        usePrescriptionStore.getState().setMedicinePbmProgramData(medicineData.IdMedicamento, updatedProgramData);
        usePrescriptionStore.getState().setMedicinePbmConsent(medicineData.IdMedicamento, true);
      }

      setStep('success');
      if (onStepChange) {
        onStepChange('success');
      }
      if (onConfirm) {
        onConfirm(consentGiven, consentGiven, email, phone);
      }
    } catch (error: unknown) {
      setIsLoading(false);
      goToError({ error });
    }
  };

  const handleClose = () => {
    if (step === 'success' || step === 'alreadyRegistered' || step === 'error') {
      onClose();
      return;
    }
    setShowCancelModal(true);
    handleEventEmission('backoffice_campaign_pbm_cancel_consent_view', {
      flow: step === 'consent' ? 'consentimento' : 'contato paciente',
    });
  };

  const handleCancelConfirm = () => {
    setShowCancelModal(false);
    if (onCancel) {
      onCancel();
    }
    setStep('consent');
    onClose();

    handleEventEmission('backoffice_campaign_pbm_cancela_precadastro_click', {
      flow: step === 'consent' ? 'consentimento' : 'contato paciente',
      continouPreCadastro: false,
    });
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    handleEventEmission('backoffice_campaign_pbm_cancela_precadastro_click', {
      flow: step === 'consent' ? 'consentimento' : 'contato paciente',
      continouPreCadastro: true,
    });
  };

  const handleEventEmission = async (eventName: string, extraParams: Record<string, unknown> = {}) => {
    if (!prescription?.idPrescricao) return;

    try {
      await TrackerEventService.track(prescription.idPrescricao, eventName, {
        idPrescricao: prescription?.idPrescricao,
        codigoValidacao: prescription?.CodigoValidacao,
        idParceiro: partnerId,
        idMedico: prescription?.Prescritor?.idPrescritor ?? prescription?.idPrescritor,
        idPaciente: prescription?.Paciente?.idPaciente,
        idMedicamento: medicineData?.IdMedicamento,
        listaEans:
          medicineData?.EANs ??
          (medicineData && 'ListaEANS' in medicineData ? medicineData.ListaEANS : []) ??
          [],
        nomeDoPrograma: getPbmBenefit(medicineData?.Beneficios)?.Nome,
        isMobile,
        tipoDePrograma: 'PBM',
        ambiente: process.env.REACT_APP_ENVIRONMENT || 'staging',
        ...extraParams,
      });
    } catch (error: unknown) {
      logger.error('[PBM - EventEmission] Erro ao emitir evento de analytics:', { eventName, error });
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'alreadyRegistered':
        return <PbmAlreadyRegisteredStep onClose={handleClose} onEventEmission={handleEventEmission} />;
      case 'success':
        return <PbmSuccessStep onClose={handleClose} onEventEmission={handleEventEmission} />;
      case 'error':
        return <PbmErrorStep onClose={handleClose} onEventEmission={handleEventEmission} />;
      case 'review':
        return (
          <PbmReviewStep
            email={email}
            phone={phone}
            isLoading={isLoading}
            controllerName={controllerName}
            onEmailChange={setEmail}
            onPhoneChange={setPhone}
            onBack={handleBackFromReview}
            onConfirm={handlePbmPreEnrollment}
            onClose={handleClose}
            onEventEmission={handleEventEmission}
          />
        );
      case 'consent':
      default:
        return (
          <PbmConsentStep
            programName={programName}
            optoutUrl={optoutUrl}
            partnerProgramUrl={partnerProgramUrl}
            controllerName={controllerName}
            consentGiven={consentGiven}
            onConsentChange={setConsentGiven}
            isLoading={isLoading}
            onConfirm={handleConsentConfirm}
            onClose={handleClose}
          />
        );
    }
  };

  return (
    <>
      <PbmCancelModal isOpen={showCancelModal} onClose={handleCancelModalClose} onConfirm={handleCancelConfirm} />

      {isMobile ? (
        <Modal isOpen={isOpen} onClose={handleClose} size="full" isCentered={false}>
          <ModalOverlay />
          <ModalContent
            maxW="100vw"
            maxH="100vh"
            m={0}
            borderRadius={0}
            height="100vh"
            display="flex"
            flexDirection="column"
          >
            <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
              {renderContent()}
            </Box>
          </ModalContent>
        </Modal>
      ) : (
        <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
          <DrawerOverlay />
          <DrawerContent>{renderContent()}</DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default PbmSidebar;
